import React, { useEffect, useState } from 'react';
import { KassaService } from 'services/KassaService';
import { message, Table, Tag } from 'antd';
import { usePagination } from 'hooks/usePagination';
import { PAGE_SIZE_OPTIONS } from 'constants/table';
import { checkValue, formatDate } from 'utils';
import {
  checkPaymentsType,
  checkPaymentsTypeMap,
  operationType,
  operationTypeMap,
} from 'utils/handbooks/kassaHandbooks';
import { useFilters } from 'hooks/useFilters';
import { priceRender } from 'utils/table';
import { SumRange } from 'components/Filters/SumRange';
import { FilterIcon } from 'components/Filters/FilterIcon';
import { RangePickerFilter } from 'components/Filters/RangePickerFilter';
import { InputFilter } from 'components/Filters/InputFilter';
import { CheckboxFilter } from 'components/Filters/CheckboxFilter';

function ChecksTable({ isActive, shiftId, onRowClick }) {
  const [loading, setLoading] = useState(false);
  const [checks, setChecks] = useState([]);
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const { filters, handleFilters, resetFilters } = useFilters({
    fixed_check: null,
    from_created_at: null,
    to_created_at: null,
    from_sum: undefined,
    to_sum: undefined,
    operation_type: null,
    payment_type: null,
  });

  const {
    fixed_check,
    from_created_at,
    to_created_at,
    from_sum,
    to_sum,
    operation_type,
    payment_type,
  } = filters;

  const columns = [
    {
      title: 'Номер чека',
      dataIndex: 'fixed_check',
      key: 'fixed_check',
      filterDropdown: () => (
        <InputFilter
          value={fixed_check}
          placeholder="Поиск по номеру"
          onFilterChange={(fixed_check) => handleFilters({ fixed_check })}
        />
      ),
      filterIcon: () => <FilterIcon search isActive={fixed_check} />,
    },
    {
      title: 'Тип',
      dataIndex: 'operation_type',
      key: 'operation_type',
      render: (value) => <Tag>{operationTypeMap[value]}</Tag>,
      filterDropdown: () => (
        <CheckboxFilter
          filter={operation_type}
          filters={operationType}
          onFilterChange={(operation_type) => handleFilters({ operation_type })}
        />
      ),
      filterIcon: () => <FilterIcon isActive={operation_type} />,
    },
    {
      title: 'Сумма',
      dataIndex: 'sum',
      key: 'sum',
      render: priceRender,
      filterDropdown: () => {
        return (
          <SumRange
            fromValue={from_sum}
            toValue={to_sum}
            getRange={(first, second) =>
              handleFilters({ from_sum: first, to_sum: second })
            }
          />
        );
      },
      filterIcon: () => <FilterIcon isActive={from_sum || to_sum} />,
    },
    {
      title: 'Время',
      dataIndex: 'created_date',
      key: 'created_date',
      render: (date) => {
        if (checkValue(date) === date)
          return formatDate(date, 'DD.MM.YYYY, HH.mm');
        return checkValue(date);
      },
      filterDropdown: () => {
        return (
          <RangePickerFilter
            value={[from_created_at, to_created_at]}
            onFilterChange={([start, end]) => {
              handleFilters({ from_created_at: start, to_created_at: end });
            }}
          />
        );
      },
      filterIcon: () => (
        <FilterIcon isActive={from_created_at && to_created_at} />
      ),
    },
    {
      title: 'Способ оплаты',
      dataIndex: 'payment_type',
      key: 'payment_type',
      render: (value) => <Tag>{checkPaymentsTypeMap[value]}</Tag>,
      filterDropdown: () => (
        <CheckboxFilter
          filter={payment_type}
          filters={checkPaymentsType}
          onFilterChange={(payment_type) => handleFilters({ payment_type })}
        />
      ),
      filterIcon: () => <FilterIcon isActive={payment_type} />,
    },
  ];

  useEffect(() => {
    return () => {
      resetPagination();
      resetFilters();
    };
  }, []);

  useEffect(() => {
    fetchChecks();
  }, [shiftId, page, perPage, filters]);

  const fetchChecks = async () => {
    if ((!isActive && !shiftId) || !page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await KassaService.getChecks(
        shiftId,
        page,
        perPage,
        filters
      );

      setTotalRows(total);
      setChecks(items);
    } catch (err) {
      message.error('Не удалось загрузить чеки по этой смене');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Table
      className="check-table"
      rowKey="id"
      loading={loading}
      dataSource={checks}
      columns={columns}
      pagination={{
        current: page,
        pageSize: perPage,
        total: totalRows,
        showSizeChanger: true,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
      onRow={(record) => {
        return {
          onDoubleClick: () => onRowClick(record, 'checks'),
        };
      }}
      onChange={setPagination}
    />
  );
}

export { ChecksTable };
